import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Youtube, Star, ExternalLink,
} from "lucide-react";
import { EditableText } from "./live/Editable";

const SOCIAL = {
  facebook: "https://www.facebook.com/kikicentre",
  instagram: "https://www.instagram.com/kikicentre",
  linkedin: "https://www.linkedin.com/company/kiki-centre-for-technology",
  youtube: "https://www.youtube.com/@kikicentre9707",
};

const ADDRESS = "1/47, Sector 75A, Gurugram, Haryana 122012";
const GOOGLE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  "Kiki Centre for Technology, " + ADDRESS
)}`;

export default function Footer() {
  return (
    <footer className="relative bg-[#04102e] text-slate-300 overflow-hidden">
      {/* Wave top */}
      <svg className="w-full -mt-px" viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: 60 }}>
        <path fill="#ffffff" d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,0 L0,0 Z"></path>
      </svg>
      {/* Decorative blobs */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img
                src="/assets/logo.png"
                alt="Kiki Centre"
                className="h-16 w-auto bg-yellow-400 rounded-xl p-2 shadow-lg ring-1 ring-yellow-300"
              />
            </Link>
            <EditableText path="footer.tagline" as="p" multiline className="text-sm leading-relaxed text-slate-400">
              Skilling India's youth as per international standards with German precision and Indian heart.
            </EditableText>

            {/* Instagram highlight */}
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 text-[#0B2A6B] font-bold hover:from-yellow-300 hover:to-yellow-400 transition shadow-lg"
              data-testid="footer-instagram-cta"
            >
              <Instagram className="h-4 w-4" />
              <span className="text-sm">Follow @kikicentre</span>
              <ExternalLink className="h-3.5 w-3.5" />
            </a>

            <div className="mt-5 flex items-center gap-3">
              {[
                { Icon: Facebook, href: SOCIAL.facebook, label: "Facebook" },
                { Icon: Instagram, href: SOCIAL.instagram, label: "Instagram" },
                { Icon: Linkedin, href: SOCIAL.linkedin, label: "LinkedIn" },
                { Icon: Youtube, href: SOCIAL.youtube, label: "YouTube" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="h-9 w-9 rounded-lg bg-white/5 hover:bg-yellow-400 hover:text-[#0B2A6B] flex items-center justify-center transition"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-white font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["About Kiki", "/about"],
                ["All Courses", "/courses"],
                ["Blog", "/blog"],
                ["News & Events", "/news"],
                ["Financial Aid", "/financial-aid"],
                ["Placements", "/placement"],
                ["Admissions", "/admissions"],
                ["Contact", "/contact"],
              ].map(([l, h]) => (
                <li key={h}>
                  <Link to={h} className="hover:text-yellow-400 transition">
                    {l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Courses */}
          <div>
            <h4 className="text-white font-bold mb-4">Top Courses</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["Mechatronics", "/courses/long-term"],
                ["Precision Machining", "/courses/long-term"],
                ["CNC Programming", "/courses/short-term"],
                ["Industrial Automation", "/courses/short-term"],
                ["Tool & Die Design", "/courses/long-term"],
                ["Corporate Training", "/courses/corporate"],
              ].map(([l, h], i) => (
                <li key={i}>
                  <Link to={h} className="hover:text-yellow-400 transition">
                    {l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-4">Reach Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 text-yellow-400 shrink-0" />
                <a
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-yellow-400"
                  data-testid="footer-address-link"
                >
                  {ADDRESS}
                </a>
              </li>
              <li className="flex gap-3">
                <Phone className="h-5 w-5 text-yellow-400 shrink-0" />
                <div>
                  <a href="tel:+918800288994" className="hover:text-yellow-400 block">
                    +91 8800288994
                  </a>
                  <a href="tel:+919717283555" className="hover:text-yellow-400 block">
                    +91 9717283555
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail className="h-5 w-5 text-yellow-400 shrink-0" />
                <a href="mailto:info@kikicentre.com" className="hover:text-yellow-400">
                  info@kikicentre.com
                </a>
              </li>
              <li className="flex gap-3">
                <Star className="h-5 w-5 text-yellow-400 shrink-0 fill-yellow-400" />
                <a
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-yellow-400"
                >
                  Rate us on Google
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-sm text-slate-400">
          <p>© {new Date().getFullYear()} Kiki Centre for Technology. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/admin/login" className="hover:text-yellow-400">Admin Login</Link>
            <a href="#" className="hover:text-yellow-400">Privacy Policy</a>
            <a href="#" className="hover:text-yellow-400">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
